import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Validación asíncrona de un cupón ligada a una identidad inmutable:
 * código normalizado + plan/contexto + generación + montado.
 *
 * - Editar el código, cambiar el plan/servicio/sucursal (``context``) o
 *   desmontar invalida la operación en curso y limpia el cupón aplicado
 *   INMEDIATAMENTE (en el mismo render, sin esperar a un efecto ni al servidor).
 * - Los callbacks ``validate``/``errorMessage`` forman parte de la identidad:
 *   encapsulan actor, ruta o servicio. Si su identidad cambia con una
 *   validación en vuelo, la respuesta se descarta y el estado se limpia en el
 *   mismo render. Los llamadores deben pasarlos ESTABLES (referencias de
 *   módulo o memorizadas) e incluir actor/ruta en ``context``.
 * - La identidad se captura ANTES del ``await``; éxito, error y ``finally``
 *   solo se aplican si sigue siendo la vigente.
 */
export const normalizeCouponCode = (codigo: string) => (codigo || '').trim().toUpperCase();

export const couponIdentity = (codigo: string, context: string) => `${normalizeCouponCode(codigo)}|${context}`;

type CallbackPair<T> = [Options<T>['validate'], Options<T>['errorMessage']];

interface Options<T> {
  /** Llamada al servidor: código tal como lo escribió el cliente + plan explícito. */
  validate: (codigo: string, planId: number | null) => Promise<T>;
  errorMessage: (err: unknown) => string;
  /** Plan (``PrecioPlanServicio``) contra el que se valida; forma parte de la identidad. */
  planId: number | null | undefined;
  /** Contexto adicional (servicio, sucursal…) que también invalida al cambiar. */
  context?: string;
}

export function useCouponValidation<T>({ validate, errorMessage, planId, context = '' }: Options<T>) {
  const [codigo, setCodigoState] = useState('');
  const [aplicado, setAplicado] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validando, setValidando] = useState(false);

  const fullContext = `${planId ?? ''}|${context}`;
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  // Identidad y contexto vigentes, espejados en cada render (sin closures).
  const identityRef = useRef(couponIdentity(codigo, fullContext));
  identityRef.current = couponIdentity(codigo, fullContext);
  const contextRef = useRef(fullContext);
  contextRef.current = fullContext;

  // Cambio de plan/contexto: se limpia en el MISMO render (estado derivado),
  // de modo que ninguna continuación ni ningún clic posterior vea el cupón
  // validado bajo el contexto anterior. La identidad (que incluye el
  // contexto) es la que descarta la respuesta pendiente.
  const [contextSeen, setContextSeen] = useState(fullContext);
  if (contextSeen !== fullContext) {
    // También invalida ciclos ABA (plan A -> B -> A): comparar solo la
    // identidad textual permitiría que una respuesta antigua de A volviera a
    // parecer vigente al regresar al mismo contexto.
    generationRef.current += 1;
    setContextSeen(fullContext);
    setAplicado(null);
    setError(null);
    setValidando(false);
  }
  // Cambio de callbacks (actor/ruta/servicio encapsulados): misma política
  // render-sync. Se comparan por referencia contra los últimos vistos y solo
  // se limpia lo que realmente haya que limpiar (un callback inline que
  // cambiase en cada render invalidaría cada validación: por eso los
  // llamadores DEBEN pasar referencias estables).
  const callbacksRef = useRef<CallbackPair<T>>([validate, errorMessage]);
  if (callbacksRef.current[0] !== validate || callbacksRef.current[1] !== errorMessage) {
    callbacksRef.current = [validate, errorMessage];
    generationRef.current += 1;
    if (aplicado !== null) setAplicado(null);
    if (error !== null) setError(null);
    if (validando) setValidando(false);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  const setCodigo = useCallback((next: string) => {
    // Editar el código invalida y limpia de inmediato (aunque solo cambie una letra).
    generationRef.current += 1;
    identityRef.current = couponIdentity(next, contextRef.current);
    setCodigoState(next);
    setAplicado(null);
    setError(null);
    setValidando(false);
  }, []);

  const limpiar = useCallback(() => setCodigo(''), [setCodigo]);

  const validar = useCallback(async () => {
    const normalizado = normalizeCouponCode(codigo);
    if (!normalizado) return;
    const identity = couponIdentity(codigo, fullContext);
    const generation = ++generationRef.current;
    const plan = planId ?? null;
    const isCurrent = () => mountedRef.current
      && generation === generationRef.current
      && identityRef.current === identity;
    setValidando(true);
    setError(null);
    setAplicado(null);
    try {
      const result = await validate(codigo, plan);
      if (!isCurrent()) return;
      setAplicado(result);
    } catch (err) {
      if (!isCurrent()) return;
      setAplicado(null);
      setError(errorMessage(err));
    } finally {
      if (isCurrent()) setValidando(false);
    }
  }, [codigo, fullContext, planId, validate, errorMessage]);

  return { codigo, normalizado: normalizeCouponCode(codigo), setCodigo, aplicado, error, validando, validar, limpiar };
}
